// Workflow shell — left sidebar (5-group collapsible nav, commonsku-style
// IA) + header (user menu dropdown) + outlet for the page content.
// Editorial layout: generous whitespace, no tight chrome.
//
// Nav IA (locked 2026-05-18):
//   • Sales       — Dashboard / Projects / Accounts / Company stores
//                   (Presentations live INSIDE a Project — Project container
//                    page sub-nav. Project requests + self-registered backlog
//                    surface via Command Center backlog tiles.)
//   • Production  — Dashboard / Production Report
//   • Finance     — Dashboard / Billing / Invoicing
//
// IA update 2026-05-29: "Quotes" removed from the sidebar
// (project-only access). Estimates + Quotes are viewed inside
// their project's sub-nav (/projects/:id/{estimate|sales-order}/:orderId);
// legacy /orders links redirect into that context via OrderRedirect.
//   • Management  — Dashboard / Analytics
//   • Reports     — (top-level, single page)
//
// Admin surfaces (Catalog / Categories / Colors / Access) are intentionally
// NOT in this nav. They live in Command Center at `/command/dashboard`.
// Settings lives in the user-menu dropdown in the header.
//
// Group open/closed state persists in localStorage so reps see the same
// expansion state across sessions. Default-open if the current route is
// inside the group.
import { useEffect, useRef, useState } from "react";
import { NotificationBell } from "@/components/NotificationBell";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Wrench,
  Banknote,
  LineChart,
  FileText,
  Mail,
  Settings,
  LogOut,
  Menu as MenuIcon,
  X,
  ChevronDown,
  ChevronRight,
  Archive,
} from "lucide-react";
import { FlowLockup } from "@/components/FlowLockup";
import { GlobalSearchBar } from "@/components/search/GlobalSearchBar";
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";
import { isFinanceRole, usePlanStatus } from "@/lib/compPlans";
import { isMarketingOnly, roleWords } from "@/lib/roles";

interface NavSubItem {
  to: string;
  label: string;
  soon?: boolean;
  /** When set, the item renders only for users holding one of these
   *  roles — keep in lockstep with the route's requireAnyRole gate, or a
   *  locked-out user clicks into a 403 dressed as an empty state. */
  roles?: string[];
}

interface NavGroup {
  key: string;
  label: string;
  icon: typeof Wrench;
  items: NavSubItem[];
}

const navGroups: NavGroup[] = [
  {
    key: "sales",
    label: "Sales",
    icon: TrendingUp,
    items: [
      { to: "/sales/dashboard", label: "Dashboard" },
      { to: "/sales/pipeline", label: "Pipeline" },
      { to: "/sales/website-orders", label: "Website orders" },
      { to: "/projects", label: "Opportunities" },
      { to: "/clients", label: "Accounts" },
      { to: "/virtuals", label: "Virtuals", roles: ["admin", "client_rep", "order_rep", "production_rep"] },
      { to: "/messages", label: "Messages", roles: ["admin", "client_rep", "order_rep"] },
      { to: "/sales/company-stores", label: "Company stores" },
    ],
  },
  {
    key: "production",
    label: "Production",
    icon: Wrench,
    items: [
      { to: "/production/dashboard", label: "Dashboard", roles: ["admin", "production_rep", "client_rep", "order_rep"] },
      { to: "/production/report", label: "Production Report" },
      { to: "/production/vendors", label: "Vendors" },
    ],
  },
  {
    key: "finance",
    label: "Finance",
    icon: Banknote,
    items: [
      { to: "/finance/dashboard", label: "Dashboard", roles: ["admin", "finance"] },
      { to: "/finance/billing", label: "Billing" },
      { to: "/finance/invoicing", label: "Invoicing" },
      { to: "/finance/vendor-cafe", label: "Vendor Cafe" },
      { to: "/finance/commissions", label: "Commissions" },
    ],
  },
  {
    key: "management",
    label: "Management",
    icon: LineChart,
    items: [
      { to: "/management/dashboard", label: "Dashboard", roles: ["admin"] },
      { to: "/management/analytics", label: "Analytics", soon: true },
    ],
  },
];

// Single-page top-level nav items (no sub-menu).
interface NavSingle {
  key: string;
  to: string;
  label: string;
  icon: typeof FileText;
  soon?: boolean;
  /** Only these roles see it (omitted = everyone). */
  roles?: string[];
}

const navSingles: NavSingle[] = [
  { key: "reports", to: "/reports", label: "Reports", icon: FileText },
  // Read-only commonsku archive (CEO 2026-10-16).
  { key: "cs-import", to: "/cs-import", label: "CS Import", icon: Archive },
  // Admin in the sidebar too, admins only (Stephanie 2026-10-27).
  { key: "admin", to: "/command/dashboard", label: "Admin", icon: Wrench, roles: ["admin"] },
];

const STORAGE_KEY = "merchd.nav.groupState";

function loadGroupState(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveGroupState(state: Record<string, boolean>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

export function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // The Marketing role reads Reports only in Flow (CEO 2026-10-21); the rest
  // lives in Hub ▸ Marketing. The server enforces it (services/staff-access.ts).
  const marketingOnly = isMarketingOnly(user?.roles);
  const shownGroups = marketingOnly ? [] : navGroups;
  const shownSingles = (marketingOnly ? navSingles.filter((s) => s.key === "reports") : navSingles).filter(
    (s) => !s.roles || s.roles.some((r) => user?.roles.includes(r)),
  );

  // Group expand/collapse state. Default-open if the active route is
  // inside the group; otherwise honor persisted localStorage.
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>(() => {
    const persisted = loadGroupState();
    const next: Record<string, boolean> = { ...persisted };
    for (const g of navGroups) {
      if (next[g.key] === undefined) {
        next[g.key] = g.items.some((i) => location.pathname.startsWith(i.to));
      }
    }
    return next;
  });

  useEffect(() => {
    saveGroupState(groupOpen);
  }, [groupOpen]);

  // Auto-open the group when the user navigates into one of its routes.
  useEffect(() => {
    const next: Record<string, boolean> = { ...groupOpen };
    let changed = false;
    for (const g of navGroups) {
      if (g.items.some((i) => location.pathname.startsWith(i.to))) {
        if (!next[g.key]) {
          next[g.key] = true;
          changed = true;
        }
      }
    }
    if (changed) setGroupOpen(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Commonsku-style: on a project detail page the global nav collapses to an
  // icon rail (desktop only) so the project workspace reclaims the screen
  // width. Mobile keeps the full slide-out. Section icons still navigate —
  // clicking one leaves the project and the nav expands again.
  const onProjectPage = /^\/projects\/[^/]+/.test(location.pathname);
  // The Production Report folds the menu too, to make room for its columns
  // (Stephanie 2026-11-04: "just do on the production report screen for now").
  const foldsMenu = onProjectPage || location.pathname.startsWith("/production/report");
  // Hovering the folded rail opens the full menu OVER the page (Stephanie
  // 2026-11-02: clicking the "f" took her to the dashboard, and hovering did
  // nothing, so she couldn't get to Vendors from a project). Moving away folds
  // it back; the page underneath doesn't shift.
  const [railHover, setRailHover] = useState(false);
  useEffect(() => setRailHover(false), [location.pathname]);
  const railCollapsed = foldsMenu && !railHover;

  // Finance → Commissions (CEO 2026-10-15): once compensation plans are on,
  // staff outside Finance/admin get their own statement ("My commissions").
  const planStatus = usePlanStatus(user != null);
  const commissionsToStatement = planStatus.data?.ready === true && !isFinanceRole(user?.roles);
  const navItemFor = (item: NavSubItem): NavSubItem =>
    commissionsToStatement && item.to === "/finance/commissions"
      ? { to: "/finance/commissions/statement", label: "My commissions" }
      : item;

  return (
    <div className="flex min-h-screen bg-canvas">
      {/* Mobile slide-out backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-brown-500/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* On project pages the rail sits over the page (fixed); this keeps its
          64px place so the page doesn't shift when the menu opens on hover. */}
      {foldsMenu && <div className="hidden w-16 shrink-0 md:block" aria-hidden />}

      {/* Sidebar */}
      <aside
        onMouseEnter={foldsMenu ? () => setRailHover(true) : undefined}
        onMouseLeave={foldsMenu ? () => setRailHover(false) : undefined}
        onFocus={foldsMenu ? () => setRailHover(true) : undefined}
        className={cn(
          // Stays in view while the page scrolls (Stephanie 2026-10-28).
          "fixed inset-y-0 left-0 z-40 flex w-[220px] flex-col border-r border-rule bg-white transition-all md:translate-x-0",
          foldsMenu ? "md:fixed md:h-screen" : "md:sticky md:top-0 md:h-screen md:self-start",
          railCollapsed && "md:w-16",
          foldsMenu && railHover && "md:shadow-xl",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div
          className={cn(
            "flex h-[96px] items-center",
            railCollapsed
              ? "justify-between px-[30px] md:justify-center md:px-0"
              : "justify-between px-[30px]",
          )}
        >
          <Link
            to="/sales/dashboard"
            className="flex items-center"
            aria-label="Flow home"
          >
            <FlowLockup width={96} className={cn("text-ink", railCollapsed && "md:hidden")} />
            {railCollapsed && (
              <span className="hidden h-8 w-8 items-center justify-center rounded-md bg-ink text-base font-bold text-white md:flex">
                f
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="text-brown-300 hover:text-brown-500 md:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Collapsed icon rail — desktop, project pages only. */}
        {railCollapsed && (
          <nav className="hidden flex-1 flex-col items-center gap-1 overflow-y-auto p-2 md:flex">
            {shownGroups.map((group) => {
              const active = group.items.some((i) =>
                location.pathname.startsWith(i.to),
              );
              return (
                <NavLink
                  key={group.key}
                  to={group.items[0]!.to}
                  title={group.label}
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-md transition-colors",
                    active
                      ? "bg-peach-400 text-white"
                      : "text-brown-400 hover:bg-cream-100 hover:text-brown-500",
                  )}
                >
                  <group.icon className="h-5 w-5" aria-hidden />
                  <span className="sr-only">{group.label}</span>
                </NavLink>
              );
            })}
            {shownSingles.map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-md transition-colors",
                    active
                      ? "bg-peach-400 text-white"
                      : "text-brown-400 hover:bg-cream-100 hover:text-brown-500",
                  )}
                >
                  <item.icon className="h-5 w-5" aria-hidden />
                  <span className="sr-only">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        )}

        <nav
          className={cn(
            "flex flex-1 flex-col overflow-y-auto px-[18px] pb-4",
            railCollapsed && "md:hidden",
          )}
        >
          {shownGroups.map((group) => {
            const open = groupOpen[group.key] === true;
            const Chevron = open ? ChevronDown : ChevronRight;
            return (
              <div key={group.key} className="flex flex-col">
                <button
                  type="button"
                  onClick={() =>
                    setGroupOpen((s) => ({ ...s, [group.key]: !s[group.key] }))
                  }
                  className={cn(
                    // Section titles bold and dark so they read at a glance
                    // (Stephanie 2026-10-28).
                    "mt-4 flex min-h-[32px] w-full items-center gap-2 rounded px-3 text-[13px] font-bold uppercase tracking-[0.1em] text-ink transition-colors hover:text-brown-400",
                  )}
                  aria-expanded={open}
                >
                  <span className="flex-1 text-left">{group.label}</span>
                  <Chevron className="h-3.5 w-3.5" aria-hidden />
                </button>
                {open && (
                  <div className="mt-1 flex flex-col gap-[3px]">
                    {group.items
                      .map(navItemFor)
                      .filter(
                        (item) =>
                          !item.roles ||
                          item.roles.some((r) => user?.roles.includes(r)),
                      )
                      .map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        // Exact match when a sibling lives under this path
                        // (/production vs /production/dashboard) — otherwise
                        // both items light up as active.
                        end={group.items.some((o) => o.to !== item.to && o.to.startsWith(`${item.to}/`))}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "flex min-h-[40px] items-center gap-2 rounded px-3 text-sm transition-colors",
                            isActive
                              ? "bg-ink text-white"
                              : "text-ink hover:bg-canvas",
                            item.soon && "opacity-60",
                          )
                        }
                      >
                        <span className="flex-1">{item.label}</span>
                        {item.soon && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-muted">
                            Soon
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Single top-level items (Reports). */}
          {shownSingles.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "mt-4 flex min-h-[40px] items-center gap-3 rounded px-3 text-sm transition-colors",
                  isActive
                    ? "bg-ink text-white"
                    : "text-ink hover:bg-canvas",
                  item.soon && "opacity-60",
                )
              }
            >
              <item.icon className="h-4 w-4" aria-hidden />
              <span className="flex-1">{item.label}</span>
              {item.soon && (
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted">
                  Soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div
          className={cn(
            "px-[30px] pb-6 pt-4 text-xs text-muted",
            railCollapsed && "md:hidden",
          )}
        >
          <span className="block font-medium text-ink">Good work. Together.</span>
          other · workflow
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col md:pl-0">
        <header className="sticky top-0 z-20 flex h-[68px] items-center gap-4 border-b border-rule bg-white px-4 md:px-8">
          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="text-brown-400 hover:text-brown-500"
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
            <FlowLockup width={60} className="text-ink" />
          </div>
          {/* Global search — persistent across every route (PR #137). */}
          <div className="flex-1">
            {/* Search returns bills + POs (supplier cost) — not for the Marketing role. */}
            {!marketingOnly && <GlobalSearchBar />}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {user && <NotificationBell />}
            {user && (
              <UserMenu
                userName={user.name ?? user.email}
                userRoles={user.roles}
                onLogout={async () => {
                  await logout();
                  navigate("/login");
                }}
              />
            )}
          </div>
        </header>
        <main
          className={cn(
            "mx-auto w-full flex-1 px-4 py-8",
            // The Production Report uses the whole screen width (Stephanie 2026-10-12).
            // …and so does every other screen (CEO 2026-10-14: the side margins
            // on a wide screen made everything small and hard to read; the
            // full-width quote was "MUCH better").
            location.pathname.startsWith("/production/report") ? "max-w-none md:px-4" : "max-w-none md:px-8",
          )}
        >
          <Outlet />
        </main>
      </div>

      {/* Floating dogfood feedback widget (PR #138). Sends an email
          to Jake with the rep's note + page URL + page title. */}
      <FeedbackWidget />
    </div>
  );
}

interface UserMenuProps {
  userName: string;
  userRoles: string[];
  onLogout: () => void | Promise<void>;
}

function UserMenu({ userName, userRoles, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 rounded-md px-2 py-1 transition-colors hover:bg-cream-100"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="hidden text-right md:block">
          <div className="text-sm font-semibold text-brown-500">{userName}</div>
          <div className="text-xs text-brown-300">{roleWords(userRoles)}</div>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-sm font-semibold text-white">
          {userName.charAt(0).toUpperCase()}
        </div>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-lg border border-brown-100 bg-white shadow-lg"
        >
          <div className="border-b border-brown-50 px-4 py-3">
            <div className="text-sm font-semibold text-brown-500">{userName}</div>
            <div className="mt-0.5 text-xs text-brown-300">
              {roleWords(userRoles)}
            </div>
          </div>
          <NavLink
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-brown-400 transition-colors hover:bg-cream-100 hover:text-brown-500"
            role="menuitem"
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </NavLink>
          {/* Admin from the menu, no separate link (Stephanie 2026-10-26). */}
          {userRoles.includes("admin") && (
            <NavLink
              to="/command/dashboard"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-brown-400 transition-colors hover:bg-cream-100 hover:text-brown-500"
              role="menuitem"
            >
              <Wrench className="h-4 w-4" />
              <span>Admin</span>
            </NavLink>
          )}
          <NavLink
            to="/settings/proof-templates"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-brown-400 transition-colors hover:bg-cream-100 hover:text-brown-500"
            role="menuitem"
          >
            <Mail className="h-4 w-4" />
            <span>Proof email templates</span>
          </NavLink>
          <NavLink
            to="/settings/po-templates"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-brown-400 transition-colors hover:bg-cream-100 hover:text-brown-500"
            role="menuitem"
          >
            <Mail className="h-4 w-4" />
            <span>PO email templates</span>
          </NavLink>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void onLogout();
            }}
            className="flex w-full items-center gap-3 border-t border-brown-50 px-4 py-2.5 text-left text-sm text-brown-400 transition-colors hover:bg-cream-100 hover:text-brown-500"
            role="menuitem"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
